import { Component, inject } from '@angular/core';
import type { ActionResult } from '@ntap/core';
import { map, type Observable, tap, switchMap, filter } from 'rxjs';
import { ItemsListActionComponent } from '../items-list-action/items-list-action.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { calculateDisabledStateAndTooltipForSelectedJedinice } from '@app/shared/util/jedinica-gradiva-security-utils';
import { withTooltipState } from '@app/shared/mixins/with-tooltip-state.mixin';

export interface EkulturaDialogResult {
  status: 'new' | 'updated';
  includeChildren: boolean;
  includeFiles: boolean;
}

@Component({
  selector: 'app-posalji-u-ekulturu-action',
  templateUrl: '../../../shared/components/actions/common-action-template.html',
  styleUrls: ['./posalji-u-ekulturu-action.component.scss']
})
export class PosaljiUEkulturuActionComponent extends withTooltipState(
  ItemsListActionComponent
) {
  private dynamicDialogService = inject(DialogService);

  protected override initDisabledObservable(): void {
    this.isDisabled$ = this.listViewProvider.selectedItems$.pipe(
      map((selectedItems) => {
        const isDisabledBecauseSystem =
          this.isDisabledActionForNerazvrstano(selectedItems);
        const [publishable, tooltip] =
          calculateDisabledStateAndTooltipForSelectedJedinice(selectedItems);
        if (isDisabledBecauseSystem) {
          this.setTooltip(
            'Nerazvrstano gradivo nije moguće objaviti u eKultura sustavu'
          );
        } else if (!publishable) {
          this.setTooltip(tooltip);
        }
        return selectedItems.length <= 0 || !publishable;
      })
    );
  }

  protected override doExecute(): Observable<ActionResult> {
    const ref = this.dynamicDialogService.open(ConfirmEkulturaDialog, {
      header: 'Slanje u eKulturu'
    });

    return (ref.onClose as Observable<EkulturaDialogResult | undefined>).pipe(
      filter((result) => result !== undefined),
      switchMap((result) => {
        return super
          .doExecuteWithParams({
            $status: result!.status,
            $includeChildren: String(result!.includeChildren),
            $includeFiles: String(result!.includeFiles)
          })
          .pipe(
            tap(() => {
              this.notificationService.success(
                'Označeno gradivo je zakazano za prijenos u eKultura sustav.'
              );
            })
          );
      })
    );
  }

  private isDisabledActionForNerazvrstano(selectedItems: object[]): boolean {
    return selectedItems.some(
      (item: any) =>
        item['en~jedinica_gradiva~arhivska_oznaka']?.indexOf('Nerazvrstano-') >
        0
    );
  }
}

@Component({
  selector: 'app-confirm-ekultura-dialog',
  template: `
    <div
      class="flex flex-column align-items-stretch p-5 surface-overlay border-round"
    >
      <p class="mb-0">Odaberite status paketa za slanje u eKulturu</p>

      <div class="mt-4">
        <p class="mb-2 font-medium">Opseg slanja</p>
        <div class="flex flex-column gap-2">
          <div class="field-radiobutton mb-0">
            <p-radioButton
              name="ekulturaScope"
              [value]="true"
              [(ngModel)]="includeChildren"
              inputId="ekulturaScopeChildren"
            ></p-radioButton>
            <label for="ekulturaScopeChildren" class="ml-2">
              Odabranu jedinicu i sve niže jedinice gradiva
            </label>
          </div>
          <div class="field-radiobutton mb-0">
            <p-radioButton
              name="ekulturaScope"
              [value]="false"
              [(ngModel)]="includeChildren"
              inputId="ekulturaScopeOnly"
            ></p-radioButton>
            <label for="ekulturaScopeOnly" class="ml-2">
              Samo odabranu jedinicu
            </label>
          </div>
        </div>
        <small class="block mt-2 text-color-secondary">
          Fond ili zbirka može imati veliki broj nižih jedinica i datoteka.
          Odaberite 'Samo odabranu jedinicu' ako ne želite sve odjednom slati na
          eKulturu.
        </small>
      </div>

      <div class="mt-4">
        <p class="mb-2 font-medium">Opseg podataka</p>
        <div class="flex flex-column gap-2">
          <div class="field-radiobutton mb-0">
            <p-radioButton
              name="ekulturaContent"
              [value]="true"
              [(ngModel)]="includeFiles"
              inputId="ekulturaContentWithFiles"
            ></p-radioButton>
            <label for="ekulturaContentWithFiles" class="ml-2">
              Metapodatke i datoteke
            </label>
          </div>
          <div class="field-radiobutton mb-0">
            <p-radioButton
              name="ekulturaContent"
              [value]="false"
              [(ngModel)]="includeFiles"
              inputId="ekulturaContentMetadataOnly"
            ></p-radioButton>
            <label for="ekulturaContentMetadataOnly" class="ml-2">
              Samo metapodatke
            </label>
          </div>
        </div>
        <small class="block mt-2 text-color-secondary">
          Odaberite 'Samo metapodatke' ako želite poslati samo opisne podatke
          bez priloženih datoteka.
        </small>
      </div>

      <div class="flex align-items-stretch gap-2 mt-4 justify-content-center">
        <button
          pButton
          label="Pošaljite zapise kao nove"
          (click)="accept('new')"
          class="w-8rem"
        ></button>

        <button
          pButton
          label="Pošaljite zapise kao ažurirane"
          (click)="accept('updated')"
          class="w-8rem"
        ></button>

        <button
          pButton
          label="Odustani"
          (click)="reject()"
          class="p-button-outlined w-8rem"
        ></button>
      </div>
    </div>
  `
})
export class ConfirmEkulturaDialog {
  private ref = inject(DynamicDialogRef);

  public includeChildren = true;
  public includeFiles = true;

  public accept(status: 'new' | 'updated') {
    this.ref.close({
      status,
      includeChildren: this.includeChildren,
      includeFiles: this.includeFiles
    });
  }

  public reject() {
    this.ref.close();
  }
}
